import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { ref, onValue, off, get } from "firebase/database";
import { database } from "../config";


export const fetchUserData = createAsyncThunk(
  "userData/fetchUserData",
  async (uid, { dispatch }) => {
    const userRef = ref(database, `users/${uid}/`);
    const listeners = [];
    return new Promise((resolve, reject) => {
      const unsubscribe = onValue(
        userRef,
        async (snapshot) => {
          const userData = snapshot.val();
          let enrichedUserData = { ...userData };

          if (!userData?.is_educator && (userData?.student?.classes || userData?.student?.refunds)) {
            const classEntries = Object.entries(userData.student.classes || {});
            const refundEntries = Object.entries(userData.student.refunds || {});
            const enrichedClasses = { ...enrichedUserData.student?.classes };
            const enrichedRefunds = { ...enrichedUserData.student?.refunds };
            const totalFetches = (classEntries.length + refundEntries.length) * 3
            let completedFetches = 0;
            let initialDispatchDone = false;
            const applyFilterRule = () => {
              // Group classes by course_id
              const courseGrouped = {};
              for (const [class_id, classInfo] of Object.entries(enrichedClasses)) {
                const { course_id } = classInfo;
                if (!course_id) continue;
                if (!courseGrouped[course_id]) courseGrouped[course_id] = [];
                courseGrouped[course_id].push([class_id, classInfo]);
              }
              // Apply filter
              const filteredClasses = {};
               // eslint-disable-next-line
              for (const [course_id, classes] of Object.entries(courseGrouped)) {
                const hasSuccess = classes.some(([_, c]) => c.payment_status === "success");

                for (const [class_id, classInfo] of classes) {
                  if (hasSuccess) {
                    if (classInfo.payment_status !== "failed") {
                      filteredClasses[class_id] = classInfo;
                    }
                  } else {
                    filteredClasses[class_id] = classInfo;
                  }
                }
              }
              return filteredClasses;
            };

            const tryDispatch = () => {
              completedFetches++;
              if (completedFetches === totalFetches && !initialDispatchDone) {
                initialDispatchDone = true;
                const filteredClasses = applyFilterRule();
                // sort refunds by refund_date (newest first)
                const sortedRefunds = Object.fromEntries(
                  Object.entries(enrichedRefunds).sort(
                    (a, b) => Number(b[1]?.refund_date || 0) - Number(a[1]?.refund_date || 0)
                  )
                );

                enrichedUserData.student = {
                  ...enrichedUserData.student,
                  classes: filteredClasses,
                  refunds: sortedRefunds,
                };
                dispatch(setUserData({ ...enrichedUserData }));
                resolve(enrichedUserData);
              } else if (initialDispatchDone) {
                const filteredClasses = applyFilterRule()
                const sortedRefunds = Object.fromEntries(
                  Object.entries(enrichedRefunds).sort(
                    (a, b) => Number(b[1]?.refund_date || 0) - Number(a[1]?.refund_date || 0)
                  )
                )
                dispatch(setUserData({
                  ...enrichedUserData,
                  student: {
                    ...enrichedUserData.student,
                    classes: filteredClasses,
                    refunds: sortedRefunds
                  }
                }));
              }
            };

            const enrichItem = (target, item_id, itemInfo) => {
              const { educator_id, course_id } = itemInfo;
              const actualClassId = itemInfo.__classId || item_id;

              if (!educator_id || !course_id) {
                tryDispatch(); tryDispatch(); tryDispatch();
                return;
              }
              const educatorRef = ref(database, `users/${educator_id}/`);
              const courseRef = ref(database, `users/${educator_id}/educator/courses/${course_id}`);
              const classRef = ref(database, `users/${educator_id}/educator/courses/${course_id}/courseDetails/class_schedule/${actualClassId}`);

              const educatorListener = onValue(educatorRef, (educatorSnap) => {
                const educatorData = educatorSnap.val() || {};
                target[item_id] = {
                  ...target[item_id],
                  educator: `${(educatorData?.account?.first_name || "").trim()} ${(educatorData.account?.last_name || "").trim()}`.trim(),
                };
                tryDispatch()
              });
              listeners.push(() => off(educatorRef, "value", educatorListener));

              const courseListener = onValue(courseRef, (courseSnap) => {
                const courseData = courseSnap.val() || {};
                target[item_id] = {
                  ...target[item_id],
                  cost: courseData?.courseDetails?.basics?.cost || "",
                  course_status: courseData?.status || "",
                  course_title: courseData?.courseDetails?.basics?.title || ""
                };
                tryDispatch()
              });
              listeners.push(() => off(courseRef, "value", courseListener));

              const classListener = onValue(classRef, (classSnap) => {
                const classData = classSnap.val() || {};
                if (!classData || Object.keys(classData).length === 0) {
                  delete target[item_id];
                  tryDispatch();
                  return;
                }
                target[item_id] = {
                  ...target[item_id],
                  times: classData?.times || [],
                  class_id: actualClassId,
                  class_status: classData?.status,
                  duration: classData?.duration || null,
                  cohort: classData?.cohort || "",
                  first_class: classData?.first_class || 0,
                  last_class: classData?.last_class || 0,
                  refund_time: classData?.refund_time || 0,
                  class_url: classData?.class_url || "",
                  class_expiration_time: classData?.class_expiration_time || 0
                };
                tryDispatch()
              });
              listeners.push(() => off(classRef, "value", classListener));
            }
            // enrich both classes and refunds (same logic)
            for (const [class_id, classInfo] of classEntries) {
              enrichItem(enrichedClasses, class_id, classInfo);
            }
            for (const [refund_id, refundInfo] of refundEntries) {
              const actualClassId = refundInfo.class_id;
              if (!actualClassId) {
                tryDispatch(); tryDispatch(); tryDispatch();
                continue;
              }
              enrichedRefunds[refund_id] = { ...refundInfo };
              enrichItem(enrichedRefunds, refund_id, {
                ...refundInfo,
                __classId: actualClassId
              });
            }
          } else {
            dispatch(setUserData({ ...enrichedUserData }));
            resolve(enrichedUserData);
          }
        },
        (error) => {
          reject(error);
        }
      );
      // Clean up all listeners when the promise is settled
      listeners.push(() => off(userRef, "value", unsubscribe));
      return () => listeners.forEach((unsubscribe) => unsubscribe());
    });
  }
)

export const fetchActiveCourses = createAsyncThunk(
  'userData/fetchActiveCourses',
  async (_, { dispatch }) => {
    const coursesRef = ref(database, 'public_courses/');

    return new Promise((resolve, reject) => {
      const unsubscribe = onValue(
        coursesRef,
        async (snapshot) => {
          const coursesData = snapshot.val();
          if (coursesData) {
            const modifiedCoursesData = {};

            const fetchEducatorData = async (courseId, course) => {
              if (course.educator_id) {
                const educatorRef = ref(database, `public_educators/${course.educator_id}`);
                const educatorSnapshot = await get(educatorRef);
                if (educatorSnapshot.exists()) {
                  const educatorData = educatorSnapshot.val();
                  return { ...course, user: { educator_id: course.educator_id, ...educatorData } };
                }
              }
              return null;
            };

            const coursePromises = Object.keys(coursesData).map(async (courseId) => {
              const course = coursesData[courseId];
              const modifiedCourse = await fetchEducatorData(courseId, course);
              if (modifiedCourse) {
                modifiedCoursesData[courseId] = modifiedCourse;
              }
            });

            await Promise.all(coursePromises);
            dispatch(setCoursesData(modifiedCoursesData));
            resolve(modifiedCoursesData);
          } else {
            dispatch(setCoursesData({}));
            resolve({});
          }
        },
        (error) => {
          reject(error);
        }
      );

      // Clean up the listener when the promise is settled
      return () => off(coursesRef, "value", unsubscribe);
    });
  }
);



const userDataSlice = createSlice({
  name: "userData",
  initialState: {
    data: null,
    error: null,
    loading: false,
    coursesData: null,
    activeCoursesError: null,
  },
  reducers: {
    setUserData: (state, action) => {
      state.data = action.payload;
    },
    setCoursesData: (state, action) => {
      state.coursesData = action.payload;
    },
    resetUserData: (state) => {
      state.data = null;
      state.error = null;
      state.loading = false;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchUserData.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchUserData.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload;
      })
      .addCase(fetchUserData.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      .addCase(fetchActiveCourses.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchActiveCourses.fulfilled, (state, action) => {
        state.loading = false;
        state.coursesData = action.payload;
      })
      .addCase(fetchActiveCourses.rejected, (state, action) => {
        state.loading = false;
        state.activeCoursesError = action.error.message;
      })
  },
});

export const { setUserData, setCoursesData, resetUserData } = userDataSlice.actions;
export default userDataSlice.reducer;
